import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, MessageCircle, Phone, Trash2, Plus, Pencil, FileDown } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LeadFormDialog, LABEL_COLOR } from "@/components/admin/LeadFormDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const DELETABLE = ["Not Interested", "Lost"];
import { buildWaLink } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const STATUSES = ["New", "Contacted", "Follow-up", "Interested", "Booked", "Not Interested", "Lost"];
const STATUS_COLOR = {
  New: "bg-sky-500/15 text-sky-700", Contacted: "bg-amber-500/15 text-amber-700",
  "Follow-up": "bg-violet-500/15 text-violet-700", Interested: "bg-primary/15 text-primary",
  Booked: "bg-secondary/15 text-emerald-600 dark:text-emerald-400", "Not Interested": "bg-muted text-muted-foreground",
  Lost: "bg-destructive/15 text-destructive",
};
const ALL = "all";

function useLeadsSynced(cb) { useEffect(() => { window.addEventListener("leads-synced", cb); return () => window.removeEventListener("leads-synced", cb); }, [cb]); }

export default function LeadsManage() {
  const { settings } = useSettings();
  const [leads, setLeads] = useState([]);
  const [trips, setTrips] = useState([]);
  const [q, setQ] = useState("");
  const [fTrip, setFTrip] = useState(ALL);
  const [fSource, setFSource] = useState(ALL);
  const [fStatus, setFStatus] = useState(ALL);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params] = useSearchParams();
  const [fEmp, setFEmp] = useState(ALL);
  const [fLabel, setFLabel] = useState(ALL);
  const [fDest, setFDest] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [due, setDue] = useState(params.get("due") === "1");
  const [employees, setEmployees] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const labels = settings.lead_labels || ["New", "Hot", "Warm", "Cold", "Follow-up", "Converted", "Lost"];
  useEffect(() => { if (isAdmin) api.get("/admin/employees").then(({ data }) => setEmployees(data)); }, [isAdmin]);
  const filterParams = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (fSource !== ALL) p.set("source", fSource);
    if (fStatus !== ALL) p.set("status", fStatus);
    if (fEmp !== ALL) p.set("employee_id", fEmp);
    if (fLabel !== ALL) p.set("label", fLabel);
    if (fDest) p.set("destination", fDest);
    if (fFrom) p.set("date_from", fFrom);
    if (fTo) p.set("date_to", fTo);
    if (due) p.set("due", "1");
    return p;
  };
  const exportExcel = () => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export?${filterParams()}`, "_blank"); };

  const del = async (l) => {
    try { await api.delete(`/admin/leads/${l.id}`); toast.success("Lead deleted"); if (sel?.id === l.id) setSel(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not delete"); }
  };
  const purge = async () => {
    const { data } = await api.post("/admin/leads/purge"); toast.success(`${data.deleted} lead(s) deleted`); load();
  };
  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (fTrip !== ALL) p.set("trip_id", fTrip);
    if (fSource !== ALL) p.set("source", fSource);
    if (fStatus !== ALL) p.set("status", fStatus);
    const sp = filterParams();
    if (fTrip !== ALL) sp.set("trip_id", fTrip);
    api.get(`/staff/leads?${sp.toString()}`).then(({ data }) => setLeads(data)).catch(() => {});
  };
  useLeadsSynced(load);
  useEffect(() => { api.get("/admin/trips").then(({ data }) => setTrips(data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, fTrip, fSource, fStatus, fEmp, fLabel, fDest, fFrom, fTo, due]);

  const updateStatus = async (lead, status) => {
    await api.patch(`/staff/leads/${lead.id}`, { status });
    toast.success("Status updated"); load();
    setSel((s) => s && s.id === lead.id ? { ...s, status } : s);
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.patch(`/staff/leads/${sel.id}`, { note });
    setSel(data); setNote(""); toast.success("Note added"); load();
  };

  const sources = settings.lead_sources || ["Instagram", "Facebook", "WhatsApp", "Google", "Website", "Referral", "Other"];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-heading text-4xl font-semibold">{isAdmin ? "Leads" : "My Leads"}</h1><p className="text-muted-foreground">{leads.length} leads{due ? " · follow-ups due" : ""}</p></div>
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-full" onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-lead"><Plus className="h-4 w-4 mr-1" /> Add Lead</Button>
          {isAdmin && <Button variant="outline" className="rounded-full" onClick={exportExcel} data-testid="export-leads"><FileDown className="h-4 w-4 mr-1" /> Export Excel</Button>}
        </div>
        {isAdmin && <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="outline" className="rounded-full text-destructive border-destructive/40" data-testid="purge-leads"><Trash2 className="h-4 w-4 mr-1" /> Clear Lost & Not Interested</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete all Lost & Not Interested leads?</AlertDialogTitle><AlertDialogDescription>This permanently removes every wasted lead. Active leads are untouched.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive" onClick={purge} data-testid="confirm-purge-leads">Delete all</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>}
      </div>

      <div className="mobile-two sm:flex sm:flex-row sm:gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input className="pl-9" placeholder="Search name / phone / email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-search" /></div>
        <Select value={fTrip} onValueChange={setFTrip}><SelectTrigger className="sm:w-44"><SelectValue placeholder="Trip" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All Trips</SelectItem>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
        <Select value={fSource} onValueChange={setFSource}><SelectTrigger className="sm:w-40"><SelectValue placeholder="Source" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All Sources</SelectItem>{sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        {isAdmin && <Select value={fEmp} onValueChange={setFEmp}><SelectTrigger className="sm:w-44" data-testid="filter-employee"><SelectValue placeholder="Employee" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All employees</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>)}</SelectContent></Select>}
        <Select value={fLabel} onValueChange={setFLabel}><SelectTrigger className="sm:w-36" data-testid="filter-label"><SelectValue placeholder="Label" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All labels</SelectItem>{labels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
        <Input className="sm:w-36" placeholder="Destination" value={fDest} onChange={(e) => setFDest(e.target.value)} data-testid="filter-destination" />
        <Input type="date" className="sm:w-40" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="filter-from" /><Input type="date" className="sm:w-40" value={fTo} onChange={(e) => setFTo(e.target.value)} />
        <Button variant={due ? "default" : "outline"} className="rounded-full" onClick={() => setDue(!due)} data-testid="filter-due">Due follow-ups</Button>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger className="sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value={ALL}>All Status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="rounded-xl border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left"><tr><th className="p-3">Lead</th><th className="p-3">Destination / Trip</th><th className="p-3">Pax</th><th className="p-3">Label</th><th className="p-3">Next follow-up</th><th className="p-3">Employee</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t hover:bg-muted/30 cursor-pointer" data-testid="lead-row" onClick={() => setSel(l)}>
                <td className="p-3"><p className="font-medium">{l.name}</p><p className="text-xs text-muted-foreground">{l.whatsapp}</p></td>
                <td className="p-3">{l.destination || l.trip_name || "—"}{l.trip_name && l.destination ? <span className="block text-xs text-muted-foreground">{l.trip_name}</span> : null}<span className="block text-xs text-muted-foreground">{l.source} · {l.lead_date || (l.created_at || "").slice(0, 10)}</span></td>
                <td className="p-3">{l.pax ?? l.travellers}</td>
                <td className="p-3">{l.label ? <Badge variant="secondary" className={`rounded-full ${LABEL_COLOR[l.label] || ""}`} data-testid="lead-label-badge">{l.label}</Badge> : "—"}</td>
                <td className={`p-3 font-mono text-xs ${l.next_followup && l.next_followup <= new Date().toISOString().slice(0, 10) && !["Converted", "Lost"].includes(l.status) ? "text-red-600 dark:text-red-400 font-semibold" : ""}`}>{l.next_followup || "—"}</td>
                <td className="p-3 text-xs"><span className="font-mono text-primary">{l.employee_code || "—"}</span>{l.employee_name ? <span className="block text-muted-foreground">{l.employee_name}</span> : null}</td>
                <td className="p-3"><Badge className={`${STATUS_COLOR[l.status]} rounded-full`} variant="secondary">{l.status}</Badge></td>
                <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(l); setFormOpen(true); }} data-testid="edit-lead"><Pencil className="h-4 w-4" /></Button>
                  <a href={buildWaLink(l.whatsapp, `Hi ${l.name}, this is ${settings.brand_name || "Safarshala"}.`)} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon" className="text-[#25D366]"><MessageCircle className="h-4 w-4" /></Button></a>
                  {isAdmin && DELETABLE.includes(l.status) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive" data-testid="delete-lead"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete lead {l.name}?</AlertDialogTitle><AlertDialogDescription>This permanently removes this {l.status.toLowerCase()} lead.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive" onClick={() => del(l)} data-testid="confirm-delete-lead">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No leads found</td></tr>}
          </tbody>
        </table>
      </div>

      <LeadFormDialog open={formOpen} onOpenChange={setFormOpen} lead={editing} labels={labels} employees={employees} trips={trips} onSaved={load} />
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent data-testid="lead-detail">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-3xl">{sel.name}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-muted-foreground">WhatsApp</p><p className="font-medium">{sel.whatsapp}</p></div>
                  <div><p className="text-muted-foreground">Email</p><p className="font-medium break-all">{sel.email || "—"}</p></div>
                  <div><p className="text-muted-foreground">City</p><p className="font-medium">{sel.city || "—"}</p></div>
                  <div><p className="text-muted-foreground">Source</p><p className="font-medium">{sel.source}</p></div>
                  <div><p className="text-muted-foreground">Trip</p><p className="font-medium">{sel.trip_name}</p></div>
                  <div><p className="text-muted-foreground">Departure</p><p className="font-medium">{sel.departure_date || "—"}</p></div>
                  <div><p className="text-muted-foreground">Travellers</p><p className="font-medium">{sel.travellers}</p></div>
                  <div><p className="text-muted-foreground">Converted</p><p className="font-medium">{sel.converted_booking_id || "No"}</p></div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={sel.status} onValueChange={(v) => updateStatus(sel, v)}>
                    <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Internal Notes</Label>
                  <div className="space-y-1.5 mb-2 max-h-32 overflow-auto">
                    {(sel.notes || []).map((n, i) => <div key={i} className="rounded bg-muted px-3 py-1.5 text-xs">{n.text} <span className="text-muted-foreground">— {n.at?.slice(0, 10)}</span></div>)}
                    {(sel.notes || []).length === 0 && <p className="text-xs text-muted-foreground">No notes yet</p>}
                  </div>
                  <div className="flex gap-2"><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note…" data-testid="lead-note" /><Button onClick={addNote}>Add</Button></div>
                </div>
                <a href={buildWaLink(sel.whatsapp, `Hi ${sel.name}, this is ${settings.brand_name || "Safarshala"} regarding ${sel.trip_name}.`)} target="_blank" rel="noreferrer">
                  <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#1da851] text-white"><MessageCircle className="h-4 w-4 mr-1" /> Message on WhatsApp</Button>
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
